import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Fade,
  Paper,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RouteIcon from '@mui/icons-material/Route';
import GroupsIcon from '@mui/icons-material/Groups';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import HandshakeIcon from '@mui/icons-material/Handshake';
import api from '../api';

// Hero image
const heroImage = '/ranger-truck-hero.jpg';

const services = [
  {
    icon: LocalGasStationIcon,
    title: 'Fuel Delivery',
    description: 'Ran out of gas? We\'ll bring fuel directly to you on the roadside.',
  },
  {
    icon: BatteryChargingFullIcon,
    title: 'Jump Starts',
    description: 'Dead battery? Our volunteers will get you charged up and back on the road.',
  },
  {
    icon: TireRepairIcon,
    title: 'Tire Changes',
    description: 'Flat tire? We\'ll help you swap it out safely on the shoulder.',
  },
  {
    icon: SupportAgentIcon,
    title: 'Roadside Support',
    description: 'Stranded? We provide assistance and peace of mind when you need it most.',
  },
];

const stats = [
  {
    icon: DirectionsCarIcon,
    number: '390+',
    label: 'Motorists Assisted',
    sublabel: 'in 2024',
  },
  {
    icon: GroupsIcon,
    number: '6',
    label: 'Active Volunteers',
    sublabel: 'trained & ready',
  },
  {
    icon: RouteIcon,
    number: '160+',
    label: 'Directional Miles',
    sublabel: 'Dallas & Austin areas',
  },
  {
    icon: AccessTimeIcon,
    number: '129+',
    label: 'Service Hours',
    sublabel: 'in 2024',
  },
];

function Home() {
  const [news, setNews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await api.getNews(3);
        setNews(data);
      } catch (err) {
        console.error('Failed to fetch news:', err);
      }
    };
    fetchNews();
    setLoaded(true);
  }, []);

  return (
    <Box>
      {/* Hero Section - Government Style */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '70vh', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          bgcolor: '#003366',
        }}
      >
        {/* Hero Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${heroImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, rgba(0,51,102,0.95) 0%, rgba(0,51,102,0.8) 40%, rgba(0,51,102,0.4) 100%)',
            },
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={loaded} timeout={800}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ py: { xs: 8, md: 4 } }}>
                  {/* Logo/Badge Area */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                      px: 2,
                      py: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/thr-badge.png"
                      alt="Texas Highway Rangers"
                      sx={{ height: 50, width: 'auto' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#ffc107',
                        fontWeight: 700,
                        letterSpacing: 2,
                        fontSize: '0.85rem',
                      }}
                    >
                      FREE ROADSIDE ASSISTANCE
                    </Typography>
                  </Box>

                  <Typography
                    variant="h1"
                    sx={{
                      color: '#fff',
                      mb: 3,
                      fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' },
                      fontWeight: 800,
                      lineHeight: 1.1,
                    }}
                  >
                    Texas Highway Rangers
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      mb: 4,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      maxWidth: 550,
                    }}
                  >
                    A volunteer-driven nonprofit providing <strong>free roadside assistance</strong> to 
                    stranded motorists across the Dallas-Fort Worth and Austin metro areas. 
                    No payment. No membership. Just Texans helping Texans.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      component={Link}
                      to="/about"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: '#ffc107',
                        color: '#003366',
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 700,
                        '&:hover': {
                          bgcolor: '#ffca28',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                    <Button
                      href="https://www.paypal.com/donate/?hosted_button_id=YOUR_BUTTON_ID"
                      target="_blank"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#fff',
                        color: '#fff',
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#ffc107',
                          bgcolor: 'rgba(255,193,7,0.1)',
                        },
                      }}
                    >
                      Support Our Mission
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>

        {/* Bottom accent bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 6,
            background: 'linear-gradient(90deg, #ffc107 0%, #ff9800 50%, #f57c00 100%)',
          }}
        />
      </Box>

      {/* Program Stats Section - GDOT Style */}
      <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: '#003366',
                mb: 1,
              }}
            >
              Program Stats
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              FY 2024 Impact
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#fff',
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    height: '100%',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,51,102,0.12)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: '#e8f4fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <stat.icon sx={{ fontSize: 28, color: '#003366' }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: '#003366',
                      mb: 0.5,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.sublabel}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                variant="overline"
                sx={{ color: '#f57c00', fontWeight: 700, letterSpacing: 2 }}
              >
                What We Do
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3, color: '#003366' }}>
                Free Roadside Assistance Services
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                Our trained volunteers monitor designated highway corridors, providing 
                immediate assistance to stranded motorists. All services are provided 
                <strong> completely free of charge</strong> — no strings attached.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We're modeled after successful state programs like TxDOT's HERO program 
                and GDOT's Highway Emergency Response Operators, bringing the same 
                professional service to areas not covered by government programs.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                {services.map((service, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: '#003366',
                          boxShadow: '0 4px 20px rgba(0,51,102,0.1)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          bgcolor: '#003366',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <service.icon sx={{ fontSize: 26, color: '#ffc107' }} />
                      </Box>
                      <Typography variant="h4" sx={{ mb: 1, color: '#003366' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Coverage Area Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #003366 0%, #00264d 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: '#ffc107', fontWeight: 700, letterSpacing: 2 }}
              >
                Coverage Areas
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3, color: '#fff' }}>
                Areas We Service
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, opacity: 0.9, lineHeight: 1.8 }}
              >
                Texas Highway Rangers currently operates in two major metro areas, 
                monitoring high-traffic corridors where stranded motorists need help most.
              </Typography>

              <Stack spacing={3}>
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    borderLeft: '4px solid #ffc107',
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#ffc107', mb: 1 }}>
                    Dallas-Fort Worth Metro
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    US-75 (Central Expressway) and surrounding corridors 
                    in Collin and Dallas counties.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    borderLeft: '4px solid #ffc107',
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#ffc107', mb: 1 }}>
                    Austin Metro
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    I-35 corridor and surrounding highways in the greater Austin area.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <Box
                  component="img"
                  src="/coverage-map.png"
                  alt="Coverage Area Map"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                  onError={(e) => {
                    e.target.src = '/dallas1.png';
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works / Mission Section */}
      <Box sx={{ py: 10, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#f57c00', fontWeight: 700, letterSpacing: 2 }}
            >
              Our Mission
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2, color: '#003366' }}>
              Texans Helping Texans
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.8 }}
            >
              We believe no one should be stranded on the side of a Texas highway. 
              Our volunteer-driven approach brings neighbors together to help neighbors, 
              one mile at a time.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                icon: VolunteerActivismIcon,
                title: 'Volunteer-Driven',
                description: 'Every assist is performed by trained volunteers who donate their time and use their own vehicles to help fellow Texans.',
              },
              {
                icon: HandshakeIcon,
                title: '100% Free',
                description: 'We never charge for any service. Fuel, jump starts, tire changes — everything is provided at no cost to the motorist.',
              },
              {
                icon: GroupsIcon,
                title: 'Community Supported',
                description: 'We\'re funded entirely by donations from individuals and businesses who believe in our mission to keep Texas roads safe.',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#e8f4fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <item.icon sx={{ fontSize: 40, color: '#003366' }} />
                  </Box>
                  <Typography variant="h4" sx={{ mb: 2, color: '#003366' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Partners Section */}
      <Box sx={{ py: 10, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#f57c00', fontWeight: 700, letterSpacing: 2 }}
            >
              Our Partners
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2, color: '#003366' }}>
              Powered By Innovation
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.8 }}
            >
              We're proud to partner with industry leaders who help us keep Texas roads safe 
              and our operations running smoothly.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Waze Partner */}
            <Grid item xs={12} md={4}>
              <Paper
                component="a"
                href="https://waze.com"
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,51,102,0.12)',
                    borderColor: '#003366',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="/waze2.png"
                    alt="Waze"
                    sx={{
                      maxHeight: 80,
                      maxWidth: '80%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => { e.target.src = '/waze1.png'; }}
                  />
                </Box>
                <Typography variant="h4" sx={{ mb: 2, color: '#003366' }}>
                  Waze
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Waze provides <strong>real-time hazard reports</strong>, helping us respond to 
                  incidents faster. When volunteers stop to assist, Waze alerts nearby drivers 
                  to slow down — protecting our team and the motorists we serve.
                </Typography>
              </Paper>
            </Grid>

            {/* Code 52 Fleet Partner */}
            <Grid item xs={12} md={4}>
              <Paper
                component="a"
                href="https://www.code52fleet.com/"
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,51,102,0.12)',
                    borderColor: '#003366',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="/code52.png"
                    alt="Code 52 Fleet"
                    sx={{
                      maxHeight: 80,
                      maxWidth: '80%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography variant="h4" sx={{ mb: 2, color: '#003366' }}>
                  Code 52 Fleet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Code 52 Fleet provides <strong>professional vehicle upfitting</strong> services, 
                  ensuring our fleet meets public safety standards. From lighting to equipment 
                  mounting, they keep our vehicles ready to respond safely on Texas highways.
                </Typography>
              </Paper>
            </Grid>

            {/* Darkstar Enterprise Partner */}
            <Grid item xs={12} md={4}>
              <Paper
                component="a"
                href="https://darkstarenterprise.co/"
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,51,102,0.12)',
                    borderColor: '#003366',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="/darkstarenterpriseday.png"
                    alt="Darkstar Enterprise Company"
                    sx={{
                      maxHeight: 80,
                      maxWidth: '80%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => { e.target.src = '/darkstarenterprise.png'; }}
                  />
                </Box>
                <Typography variant="h4" sx={{ mb: 2, color: '#003366' }}>
                  Darkstar Enterprise
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Darkstar Enterprise built <strong>RangerVault</strong> from the ground up — our 
                  custom incident management system. With real-time dispatch, GPS tracking, 
                  and analytics, it's the backbone that powers our day-to-day operations.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* News Section */}
      {news.length > 0 && (
        <Box sx={{ py: 10, bgcolor: '#f5f5f5' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{ color: '#f57c00', fontWeight: 700, letterSpacing: 2 }}
              >
                Stay Updated
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, color: '#003366' }}>
                Latest News
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {news.map((post) => (
                <Grid item xs={12} md={4} key={post.id}>
                  <Card
                    component={Link}
                    to={`/news/${post.slug}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0,51,102,0.12)',
                      },
                    }}
                  >
                    {post.imageUrl && (
                      <Box
                        sx={{
                          height: 200,
                          backgroundImage: `url(${post.imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#f57c00', fontWeight: 600 }}
                      >
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 2, color: '#003366' }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {post.excerpt}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                component={Link}
                to="/news"
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderColor: '#003366',
                  color: '#003366',
                  px: 4,
                  '&:hover': {
                    borderColor: '#003366',
                    bgcolor: 'rgba(0,51,102,0.05)',
                  },
                }}
              >
                View All News
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      {/* CTA / Support Section */}
      <Box sx={{ py: 10, bgcolor: '#fff' }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              p: { xs: 5, md: 8 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #003366 0%, #00264d 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative element */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: 'rgba(255,193,7,0.1)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,193,7,0.08)',
              }}
            />

            <Typography variant="h2" sx={{ mb: 2, color: '#fff', position: 'relative' }}>
              Support Our Mission
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.8,
                position: 'relative',
              }}
            >
              Your donation helps keep our volunteers on the road, providing free assistance 
              to stranded motorists across Texas. Every dollar goes directly to fuel, 
              equipment, and training.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ position: 'relative' }}
            >
              <Button
                variant="contained"
                size="large"
                href="https://www.paypal.com/donate/?hosted_button_id=YOUR_BUTTON_ID"
                target="_blank"
                sx={{
                  bgcolor: '#ffc107',
                  color: '#003366',
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: '#ffca28',
                  },
                }}
              >
                Donate Now
              </Button>
              <Button
                component={Link}
                to="/about"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: '#fff',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#ffc107',
                    bgcolor: 'rgba(255,193,7,0.1)',
                  },
                }}
              >
                Become a Volunteer
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* Contact / Feedback Section */}
      <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ mb: 2, color: '#003366' }}>
                Have Feedback?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                If you have comments about the Texas Highway Rangers program, or if you've been helped 
                by one of our volunteers and would like to say <strong>"thanks"</strong>, we'd love 
                to hear from you!
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                href="mailto:howdy@highwayrangers.com"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#003366',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#00264d',
                  },
                }}
              >
                Contact Us
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
